/**
 * @file: cardPrimitives.tsx
 *
 * Shared building blocks for the chat card renderers (contact / report). Each
 * primitive self-serves `rpx` — they're standalone components, not helpers
 * threaded an `rpx` argument.
 */

import { StatusPaletteKey } from '@euler/app/components/task-card/types';
import { Typography } from '@euler/app/components/typography';
import { ChatCardView } from '@euler/app/flows/chat/utils/chatCardDescriptor';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ChevronRight,
  ClipboardList,
  FileText,
  Wrench,
} from 'lucide-react-native';
import { FC, ReactNode } from 'react';
import { View } from 'react-native';
import { ChatCardShell } from '../ChatCardShell';

export const CardSurface: FC<{
  onPress?: () => void;
  /** Status palette → left accent bar (mirrors the task card). */
  accent?: StatusPaletteKey;
  children: ReactNode;
}> = ({ onPress, accent, children }) => {
  const { rpx } = useResponsiveContext();
  return (
    <ChatCardShell onPress={onPress} accent={accent}>
      <View style={{ padding: rpx(12), paddingLeft: rpx(14) }}>{children}</View>
    </ChatCardShell>
  );
};

/** Leading visual: brand logo → kind icon fallback. */
export const CardThumbnail: FC<{ view: ChatCardView }> = ({ view }) => {
  const { rpx } = useResponsiveContext();
  const side = rpx(52);
  const radius = rpx(8);
  if (view.brandLogo) {
    return (
      <View
        style={{
          width: side,
          height: side,
          borderRadius: radius,
          backgroundColor: '#F8FAFC',
          borderWidth: 1,
          borderColor: '#EEF2F7',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AdvancedImage
          uri={view.brandLogo}
          resizeMode="contain"
          style={{ width: rpx(34), height: rpx(34) }}
        />
      </View>
    );
  }
  const Icon =
    view.kind === 'report'
      ? FileText
      : view.kind === 'service_record'
      ? Wrench
      : ClipboardList;
  return (
    <View
      style={{
        width: side,
        height: side,
        borderRadius: radius,
        backgroundColor: '#EFF6FF',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon color="#3b82f6" size={rpx(24)} />
    </View>
  );
};

/** Small inline tag — plate (neutral) or report-type highlight (accent). */
export const Pill: FC<{ text: string; tone: 'plate' | 'accent' }> = ({
  text,
  tone,
}) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        paddingHorizontal: rpx(6),
        paddingVertical: rpx(1),
        borderRadius: rpx(4),
        marginRight: rpx(5),
        marginBottom: rpx(2),
        backgroundColor: tone === 'accent' ? '#EFF6FF' : '#F1F5F9',
      }}
    >
      <Typography.Text
        size={rpx(11)}
        color={tone === 'accent' ? '#2563EB' : '#475569'}
      >
        {text}
      </Typography.Text>
    </View>
  );
};

export const CardTitle: FC<{ text: string }> = ({ text }) => {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text
      size={rpx(15)}
      color="#1E293B"
      numberOfLines={1}
      style={{ fontFamily: FontFamily.NotoSans.Medium }}
    >
      {text}
    </Typography.Text>
  );
};

export const CardMeta: FC<{ text: string }> = ({ text }) => {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text
      size={rpx(12)}
      color="#64748B"
      numberOfLines={1}
      style={{ marginTop: rpx(3) }}
    >
      {text}
    </Typography.Text>
  );
};

export const CardFooter: FC<{ label: string }> = ({ label }) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        marginTop: rpx(9),
        paddingTop: rpx(7),
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography.Text size={rpx(11)} color="#94A3B8">
        {label}
      </Typography.Text>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Typography.Text size={rpx(11)} color="#CBD5E1">
          查看详情
        </Typography.Text>
        <ChevronRight color="#CBD5E1" size={rpx(14)} />
      </View>
    </View>
  );
};

/** Compact one-line chip for a card embedded in a quote/reply preview. */
export const CardQuoteChip: FC<{ icon: ReactNode; label: string }> = ({
  icon,
  label,
}) => {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(6) }}>
      {icon}
      <Typography.Text size={rpx(13)} color="#475569" numberOfLines={1}>
        {label}
      </Typography.Text>
    </View>
  );
};
